import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Menu, Skeleton } from "@mui/material";
import React from "react";

import { PLAN_PERMISSIONS } from "@/constants/modules.permissions";
import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import useGetCustomerSalesProductPayments from "@/hooks/requests/customer-sales-product-payments/use-get-customer-sales-product-payments";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useFilters from "@/hooks/use-filters";
import { ICustomerSalesProductPayment } from "@/interfaces/customer-sales-product-payment.interfaces";
import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import { PlansTableMessages } from "@/messages/customer/plans-table.messages";
import NotificationItem from "@/mui/Items/NotificationItem";
import { CustomerSalesProductStatus } from "@/types/customer-sales-product.types";

import { determineInitialFilterValues } from "../../constants";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: PlansTableMessages;
  selected: ICustomerSalesProduct;
  onCancelPlan: (plan: ICustomerSalesProduct) => void;
}

export function PlanPopover({
  isOpen,
  anchorEl,
  onClose,
  messages,
  selected,
  onCancelPlan,
}: Props) {
  const initialFilters = determineInitialFilterValues(selected.id);
  const filter = useFilters<ICustomerSalesProductPayment>({ initialValue: initialFilters });
  const filters = filter.filters;
  const noPermissionToCancel = useHasNoPermission(PLAN_PERMISSIONS.DELETE);
  const { data, isLoading } = useGetCustomerSalesProductPayments({ filters, pageNumber, pageSize });

  const onClickCancel = () => {
    onCancelPlan(selected);
    onClose();
  };

  const disabledBtn = noPermissionToCancel || selected.status !== CustomerSalesProductStatus.Active;
  const transactionReceiptUrl = data?.items?.[0]?.transactionReceiptUrl || null;
  const cancellationReceiptUrl = data?.items?.[0]?.cancellationReceiptUrl || null;

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      {isLoading ? (
        <>
          <NotificationItem
            icon={<Skeleton variant="circular" width={24} height={24} />}
            disabled
          />
          <NotificationItem
            icon={<Skeleton variant="circular" width={24} height={24} />}
            disabled
          />
        </>
      ) : (
        <>
          <NotificationItem
            onClick={() => onClickCancel()}
            icon={<DeleteIcon />}
            title={messages.cancelButton}
            disabled={disabledBtn}
          />

          {transactionReceiptUrl && (
            <NotificationItem
              icon={<ReceiptIcon />}
              disabled={false}
              onClick={() => window.open(transactionReceiptUrl, "_blank")}
              title={messages.transactionReceiptUrl}
            />
          )}

          {cancellationReceiptUrl && (
            <NotificationItem
              icon={<CancelScheduleSendIcon />}
              disabled={false}
              onClick={() => window.open(cancellationReceiptUrl, "_blank")}
              title={messages.cancellationReceiptTitle}
            />
          )}
        </>
      )}
    </Menu>
  );
}
